import React from "react";
import { Box, Typography, Stack } from "@mui/material";

interface ChartData {
  label: string;
  value: number;
  color: string;
}

interface DonutChartProps {
  data?: ChartData[];
  size?: number;
  title?: string;
}

const DonutChart = ({ data, size = 120, title }: DonutChartProps) => {
  const defaultData: ChartData[] = [
    { label: "Target coverage low", value: 35, color: "#a78bfa" },
    { label: "Max dose too high", value: 25, color: "#06b6d4" },
    { label: "OAR out of tolerance", value: 20, color: "#fb923c" },
    { label: "Max dose location", value: 15, color: "#f87171" },
    { label: "Others", value: 5, color: "#9ca3af" },
  ];

  const chartData = data || defaultData;
  const total = chartData.reduce((sum, item) => sum + item.value, 0);

  let cumulativePercentage = 0;
  const segments = chartData.map((item) => {
    const percentage = (item.value / total) * 100;
    const startAngle = (cumulativePercentage / 100) * 360;
    const endAngle = ((cumulativePercentage + percentage) / 100) * 360;

    // eslint-disable-next-line react-hooks/immutability
    cumulativePercentage += percentage;

    const largeArcFlag = percentage > 50 ? 1 : 0;
    const startAngleRad = (startAngle * Math.PI) / 180;
    const endAngleRad = (endAngle * Math.PI) / 180;

    const radius = 40;
    const innerRadius = 25;

    const x1 = 50 + radius * Math.cos(startAngleRad);
    const y1 = 50 + radius * Math.sin(startAngleRad);
    const x2 = 50 + radius * Math.cos(endAngleRad);
    const y2 = 50 + radius * Math.sin(endAngleRad);

    const x3 = 50 + innerRadius * Math.cos(endAngleRad);
    const y3 = 50 + innerRadius * Math.sin(endAngleRad);
    const x4 = 50 + innerRadius * Math.cos(startAngleRad);
    const y4 = 50 + innerRadius * Math.sin(startAngleRad);

    const pathData = [
      `M ${x1} ${y1}`,
      `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`,
      `L ${x3} ${y3}`,
      `A ${innerRadius} ${innerRadius} 0 ${largeArcFlag} 0 ${x4} ${y4}`,
      "Z",
    ].join(" ");

    return {
      ...item,
      path: pathData,
      percentage,
    };
  });

  return (
    <Box
      sx={{
        backgroundColor: "background.paper",
        paddingTop: 2,
      }}
    >
      {title && (
        <Typography variant="h6" gutterBottom sx={{ mb: 2, paddingLeft: 2 }}>
          {title}
        </Typography>
      )}

      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        {/* Donut Chart */}
        <Box sx={{ position: "relative" }}>
          <svg width={size} height={size} viewBox="0 0 100 100">
            {segments.map((segment, index) => (
              <path
                key={index}
                d={segment.path}
                fill={segment.color}
                stroke="white"
                strokeWidth="0.5"
              />
            ))}
          </svg>
        </Box>

        {/* Legend */}
        <Stack spacing={1} sx={{ maxWidth: 400 }}>
          {chartData.map((item, index) => (
            <Stack key={index} direction="row" spacing={1} alignItems="center">
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  backgroundColor: item.color,
                  flexShrink: 0,
                }}
              />
              <Typography variant="caption" sx={{ lineHeight: 1.2 }}>
                {item.label}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </Box>
    </Box>
  );
};

export default DonutChart;
